import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, Optional, Output, Self, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { of } from 'rxjs';
import { delay, take } from 'rxjs/operators';

export enum AIMS_NUMERIC_TYPES {
    PERCENT = 'percent',
    CURRENCY = 'currency',
    NUMBER = 'number'
}

export const AIMS_NUMERIC_CONTROL_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AimsNumericComponent),
    multi: true
}

@Component({
    selector: 'aims-numeric',
    exportAs: 'aimsNumeric',
    templateUrl: './aims-numeric.component.html',
    styleUrls: ['./aims-numeric.component.css']
})
export class AimsNumericComponent implements ControlValueAccessor, Validator {

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        private _changeDetector: ChangeDetectorRef) {
        if (ngControl != null) {
            // Setting the value accessor directly (instead of using
            // the providers) to avoid running into a circular import.
            ngControl.valueAccessor = this;
        }
    }

    @ViewChild('numericInput') numericInput: ElementRef;


    private _numericType: AIMS_NUMERIC_TYPES = AIMS_NUMERIC_TYPES.CURRENCY;
    @Input()
    set numericType(value:AIMS_NUMERIC_TYPES) {
      this._numericType = value;
      if (!this._maxDecimalExplicitlySet) {
        this._maxDecimals = (this.numericType == AIMS_NUMERIC_TYPES.NUMBER) ? 0 : 2;
      }
    }
    get numericType() : AIMS_NUMERIC_TYPES {
      return this._numericType;
    }

    @Input() min: number = (this.numericType == AIMS_NUMERIC_TYPES.NUMBER) ? null : 0;
    @Input() max: number = null;
    @Input() required: boolean = false;
    @Input() width: number = 0;

    private _maxDecimalExplicitlySet = false;
    private _maxDecimals: number = 2;
    @Input()
    set maxDecimals(value: number ) {
      this._maxDecimals = value;
      this._maxDecimalExplicitlySet = true;
    }
    get maxDecimals() : number {
      return this._maxDecimals;
    }

    @Output() blur: EventEmitter<number> = new EventEmitter<number>();
    @Output() change: EventEmitter<number> = new EventEmitter<number>();

    numericTypes = AIMS_NUMERIC_TYPES;

    touched = false;
    private _value: number = null;

    @Input()
    get value(): number { return this._value; }
    set value(newValue: number) {
        if (this._value !== newValue) {
            // Set this before proceeding to ensure no circular loop occurs with selection.
            this._value = newValue;
            this.onChange(this._value);
            this.markAsTouched();
        }
    }

    get errors() {
        const control = this.ngControl && this.ngControl.control;
        if (control) {
            return control.touched && control.errors;
        }
        return null;
    }

    handleFocus() {
        if (!this.numericInput.nativeElement.hasFocus()) {
            this.numericInput.nativeElement.select();
        }
    }

    handleOnBlur() {
        this.formatDisplayValue();
        this.blur.emit(this.value);
    }

    formatDisplayValue(val = this.value) {
        if ((val || val === 0) && !isNaN(val)) {
            val = (this.numericType == AIMS_NUMERIC_TYPES.PERCENT) ? val * 100 : val;
            let trim = (+(val.toFixed(this.maxDecimals))).toLocaleString('en-US', { minimumFractionDigits: this.maxDecimals, maximumFractionDigits: this.maxDecimals });
            this.numericInput.nativeElement.value = trim;
        }
    }

    // called by keyup event
    handleUpdate(value: string) {
      
      if (value !== '-' && value !== '.'){
        
        //check for negative
        let neg: boolean = value.substring(0,1) === '-';

        //strip non numeric chars
        let val = value.replace(/[^.\d]/g, '');
        val = (neg ? '-' : '') + val;

        if (val !== value) {
            this.formatDisplayValue(+val);
        }
        //If first char is . then add a 0
        if (val.substring(0, 1) == ".") {
            val = "0" + val;
        }
        //don't allow more than maxDecimals
        if (val.indexOf('.') !== -1 && val.length - val.indexOf('.') > this.maxDecimals) {
            val = val.substring(0, val.indexOf('.') + this.maxDecimals + 1);
        }
        //don't allow less than min
        let test = +val;
        if (this.min !== null && +val < this.min) {
            val = this.min.toString();
        }
        //don't allow more than max
        if (this.max !== null && +val > this.max) {
            val = this.max.toString();
        }
        //For percentage type, set to decimal value, ie - 100% = 1
        const writeVal = (this.numericType == AIMS_NUMERIC_TYPES.PERCENT) ? +val / 100 : +val;

        this.value = writeVal;
      }
     
    }

    markAsTouched() {
      if (!this.touched) {
        this.onTouched();
        this.touched = true;
      }
    }

    // ControlValueAccessor implementation

    //this method is called by the Forms module to write a value into a form control
    writeValue(val: number): void {
        this._value = val;
        this._changeDetector.markForCheck();

        of(this.value).pipe(
            delay(0),
            take(1)
        ).subscribe(v => {
          // delay so numericInput is defined
          this.formatDisplayValue();
        });
    }

    /** The method to be called to update ngModel */
    onChange = (quantity) => { console.log(quantity); };

    //  When a form value changes due to user input, we need to report the value back to the parent form.
    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    // report to the parent form that the control was touched
    onTouched: () => any = () => { };

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    isDisabled: boolean = false;
    // form controls can be enabled and disabled using the Forms API
    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this._changeDetector.markForCheck();
    }

    // End ControlValueAccessor implementation


    //Validate implementation
    validate(control: AbstractControl) {
        return control.errors;
    }

    ngOnInit(): void {
        //Override values for PERCENT
        if (this.numericType == AIMS_NUMERIC_TYPES.PERCENT) {
            this.max = 100;
            this.min = 0;
            this.maxDecimals = 2;
        }
    }

}
